import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { QRCodeSVG } from 'qrcode.react';
import { ArrowLeft, Shirt, MapPin, FileText, Printer } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONDITION_COLORS = {
  good: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
  fair: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
  needs_repair: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
};

const WardrobeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, getAuthHeaders, token } = useAuth();
  const { settings } = useSettings();
  const [wardrobe, setWardrobe] = useState(null);
  const [costumes, setCostumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const headers = isAuthenticated ? getAuthHeaders() : {};
      const wRes = await axios.get(`${API_URL}/api/wardrobes/${id}`, { headers });
      setWardrobe(wRes.data);
      if (isAuthenticated) {
        const cRes = await axios.get(`${API_URL}/api/costumes?wardrobe_id=${id}`, { headers });
        setCostumes(cRes.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Wardrobe not found');
    } finally {
      setLoading(false);
    }
  }, [id, isAuthenticated, getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePrintQR = () => {
    const qrUrl = `${window.location.origin}/wardrobe/${wardrobe.id}`;
    const w = window.open('', '_blank');
    w.document.write(`
      <!DOCTYPE html>
      <html><head><title>QR - ${wardrobe.label}</title>
      <style>body{font-family:Arial;text-align:center;padding:20px;}.c{display:inline-block;padding:20px;border:2px solid #000;}.l{font-size:24px;font-weight:bold;}.n{font-size:16px;color:#666;margin:10px 0;}@media print{body{padding:0;}}</style>
      </head><body>
      <div class="c"><div class="l">${wardrobe.label}</div><div class="n">${wardrobe.name}</div>
      <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrUrl)}" /></div>
      <script>window.onload=function(){window.print()}</script>
      </body></html>
    `);
    w.document.close();
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  
  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
      <Shirt className="w-16 h-16 text-muted-foreground mb-4" />
      <h1 className="text-2xl font-bold mb-2">Wardrobe Not Found</h1>
      <p className="text-muted-foreground mb-4">{error}</p>
      <Button onClick={() => navigate('/')} variant="outline"><ArrowLeft className="w-4 h-4 mr-2" />Go Back</Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="wardrobe-detail-page">
      <div className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}><ArrowLeft className="w-5 h-5" /></Button>
          <div className="flex items-center gap-3">
            {settings?.logo_url && <img src={settings.logo_url} alt="Logo" className="w-8 h-8 rounded object-contain" />}
            <span className="font-semibold">{settings?.school_name || 'SchoolAV'}</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <div className="flex flex-col md:flex-row gap-6">
          <Card className="border-border/50 p-4">
            <div className="bg-white p-3 rounded-lg">
              <QRCodeSVG value={`${window.location.origin}/wardrobe/${wardrobe.id}`} size={150} level="H" />
            </div>
            <Button onClick={handlePrintQR} variant="outline" size="sm" className="w-full mt-3">
              <Printer className="w-4 h-4 mr-2" />
              Print Label
            </Button>
          </Card>

          <div className="flex-1 space-y-3">
            <div>
              <code className="text-3xl font-bold font-mono">{wardrobe.label}</code>
              <h1 className="text-2xl font-semibold mt-1">{wardrobe.name}</h1>
            </div>
            {wardrobe.location && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="w-4 h-4" />{wardrobe.location}
              </div>
            )}
            {wardrobe.description && (
              <div className="flex items-start gap-2 text-muted-foreground">
                <FileText className="w-4 h-4 mt-0.5" /><p>{wardrobe.description}</p>
              </div>
            )}
            <Badge variant="secondary" className="text-sm">
              {wardrobe.costume_count} costume{wardrobe.costume_count !== 1 ? 's' : ''} inside
            </Badge>
          </div>
        </div>

        <div>
          <h2 className="text-xl font-bold mb-4">Contents</h2>
          {!isAuthenticated ? (
            <Card className="border-primary/50 bg-primary/5">
              <CardContent className="pt-6 text-center space-y-3">
                <p className="text-muted-foreground">Login to see what's inside</p>
                <Button onClick={() => navigate('/login')}>Login to Continue</Button>
              </CardContent>
            </Card>
          ) : costumes.length === 0 ? (
            <Card className="border-border/50">
              <CardContent className="py-16 text-center">
                <Shirt className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">This wardrobe is empty</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {costumes.map((c) => <CostumeCard key={c.id} costume={c} token={token} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CostumeCard = ({ costume, token }) => {
  const [photoUrl, setPhotoUrl] = useState(null);
  useEffect(() => {
    if (costume.photo_path && token) {
      axios.get(`${API_URL}/api/files/${costume.photo_path}?auth=${token}`, { responseType: 'blob' })
        .then(response => setPhotoUrl(URL.createObjectURL(response.data)))
        .catch(console.error);
    }
    return () => { if (photoUrl) URL.revokeObjectURL(photoUrl); };
    // eslint-disable-next-line
  }, [costume.photo_path, token]);

  return (
    <Card className="border-border/50 overflow-hidden">
      {photoUrl && (
        <div className="h-28 sm:h-32 bg-muted relative">
          <img src={photoUrl} alt={costume.name} className="w-full h-full object-cover" />
          <div className="absolute top-2 right-2">
            <Badge variant="secondary" className={CONDITION_COLORS[costume.condition] || CONDITION_COLORS.good}>
              {costume.condition === 'needs_repair' ? 'Needs Repair' : costume.condition.charAt(0).toUpperCase() + costume.condition.slice(1)}
            </Badge>
          </div>
        </div>
      )}
      <CardContent className="p-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold truncate flex-1">{costume.name}</h3>
          {!photoUrl && (
            <Badge variant="secondary" className={CONDITION_COLORS[costume.condition] || CONDITION_COLORS.good}>
              {costume.condition === 'needs_repair' ? 'Needs Repair' : costume.condition.charAt(0).toUpperCase() + costume.condition.slice(1)}
            </Badge>
          )}
        </div>
        <div className="text-xs text-muted-foreground mt-1 truncate">
          {costume.size_type} {costume.size_value} {costume.color && `• ${costume.color}`}
        </div>
        <div className="text-sm text-muted-foreground mt-1">Qty: <strong className="text-foreground">{costume.quantity}</strong></div>
      </CardContent>
    </Card>
  );
};

export default WardrobeDetail;
